/**
 * CORE-481 — adaptive-HLS playback for manifesto (визитка) videos.
 *
 * The encoder now emits an adaptive HLS ladder (master.m3u8 + variant playlists
 * + segments) and the API exposes it as `playbackHlsUrl`, alongside the existing
 * browser-safe MP4 in `playbackUrl`. Prefer HLS: it starts fast on a weak link
 * and adapts quality. `playbackHlsUrl` is null until the ladder job has run, so
 * every consumer must fall back to `playbackUrl` → `videoFileUrl` → `videoUrl`.
 *
 * Web trap: only Safari (and iOS WebViews) play `.m3u8` from a bare `<video>`
 * `src`; Chrome/Firefox need hls.js or they show a black screen. Native players
 * (AVPlayer/ExoPlayer via expo-video) handle HLS themselves, so on native the
 * source selection is all that's needed.
 */

/** Shape of the fields we pick a playback source from (manifesto & agape videos). */
export interface PlaybackSourceFields {
  playbackHlsUrl?: string | null;
  playbackUrl?: string | null;
  videoFileUrl?: string | null;
  videoUrl?: string | null;
}

/** The adaptive HLS manifest, or null when the ladder is not ready yet. */
export function manifestoHlsUrl(v: PlaybackSourceFields | null | undefined): string | null {
  return v?.playbackHlsUrl || null;
}

/** The non-HLS source (browser-safe MP4 → raw upload → external URL). Kept
 *  separate from the HLS url because the `#t=1` poster-frame trick and any
 *  future MP4-only path must not be handed an `.m3u8`. */
export function manifestoMp4Url(v: PlaybackSourceFields | null | undefined): string | null {
  return v?.playbackUrl || v?.videoFileUrl || v?.videoUrl || null;
}

/** Best playback source, HLS first. Safe for any player that speaks HLS
 *  (expo-video on native, or a web `<video>` driven through {@link needsHlsJs}). */
export function manifestoPlaybackUrl(v: PlaybackSourceFields | null | undefined): string | null {
  return manifestoHlsUrl(v) || manifestoMp4Url(v);
}

/** True when the url points at an HLS manifest (query/hash tolerated). */
export function isHlsUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const clean = url.split('?')[0].split('#')[0].toLowerCase();
  return clean.endsWith('.m3u8');
}

/**
 * Whether the browser can play HLS from a plain `<video src>`.
 *
 * CORE-451 — must be `=== 'probably'`, never truthiness: Chrome/Firefox answer
 * `'maybe'` for the HLS mime without being able to decode it, so a truthy test
 * silently skips hls.js and the video never starts.
 */
export function playsHlsNatively(canPlayType: (mime: string) => string): boolean {
  return canPlayType('application/vnd.apple.mpegurl') === 'probably';
}

/** True when this url is HLS AND the browser can't play it natively — i.e. we
 *  must attach hls.js instead of setting `<video src>`. */
export function needsHlsJs(url: string | null | undefined, canPlayType: (mime: string) => string): boolean {
  return isHlsUrl(url) && !playsHlsNatively(canPlayType);
}
